import { observable, computed, action } from 'mobx';
import { Sku } from './Sku';
import { copyPropertiesExcludeNullsAndGetters } from '../../utils/ObjectUtils';
import { InventoryStatus } from '../../constants/InventoryStatuses';

export interface IShadeImage {
    src: string;
    width: number;
    height: number;
}
export interface ICoreShade {
    name: string;
    description: string;
    hex_val: string;
    image: IShadeImage;
}

export interface IShadeData {
    name: string;
    description: string;
    hexVal: string;
    image: IShadeImage;
}

export class Shade {
    public name: string = '';
    public description: string = '';
    public hexVal: string = '';
    public colorFamily: string;
    @observable public perfectShadeLabel: string = '';
    @observable public isPerfectShade: boolean = false;
    @observable public isNameInsideShadeCircle: boolean = false;
    @observable public image: IShadeImage;
    @observable public isBestShade: boolean;

    @observable public colors: string[] = [];
    public parentSku: Sku | null = null;

    public static formattedShades(shades: Shade[]) {
        const newShade = new Shade();
        newShade.colors = shades.map((shade) => shade.hexVal);
        newShade.name = shades.map((shade) => shade.name).join(' / ');
        newShade.description = shades.map((shade) => shade.description).join(' / ');

        return newShade;
    }

    public static fromPlainObject(data: Shade) {
        const shade = new Shade();

        copyPropertiesExcludeNullsAndGetters(shade, data);

        return shade;
    }

    public static fromJson(data: ICoreShade) {
        const shade = new Shade();
        if (!data) {
            return shade;
        }

        copyPropertiesExcludeNullsAndGetters(shade, data);
        const shadeDataMapping = {
            hexVal: data.hex_val
        };
        copyPropertiesExcludeNullsAndGetters(shade, shadeDataMapping);

        return shade;
    }

    public toJSON(): IShadeData {
        return {
            name: this.name,
            description: this.description,
            hexVal: this.hexVal,
            image: this.image
        };
    }

    @computed
    public get isShadeAvailable() {
        if (this.parentSku !== null) {
            if (this.parentSku.inventoryStatus === InventoryStatus.Active) {
                return true;
            }
        }

        return false;
    }

    @computed
    public get inventoryStatus() {
        return this.parentSku ? this.parentSku.inventoryStatus : '';
    }

    public equals(otherShade: Shade): boolean {
        if (this.name !== otherShade.name) {
            return false;
        }

        if (this.description !== otherShade.description) {
            return false;
        }

        if (this.hexVal !== otherShade.hexVal) {
            return false;
        }

        if (this.colors !== otherShade.colors) {
            return false;
        }

        if (this.parentSku !== otherShade.parentSku) {
            return false;
        }

        return true;
    }

    @action
    public changeToPerfect(label: string = '') {
        this.showNameInsideShadeCircle();
        this.isPerfectShade = true;
        this.perfectShadeLabel = label;
    }

    @action
    public resetPerfect() {
        this.isPerfectShade = false;
        this.perfectShadeLabel = '';
        this.hideNameInsideShadeCircle();
    }

    @action
    public showNameInsideShadeCircle() {
        this.isNameInsideShadeCircle = true;
    }

    @action
    private hideNameInsideShadeCircle() {
        this.isNameInsideShadeCircle = false;
    }

    @action
    public setBestShade() {
        this.isBestShade = true;
    }
}
